import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Command } from "cmdk";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  Download,
  Plus,
  Upload,
  Bell,
  ClipboardCheck,
} from "lucide-react";
import type { Product } from "@/lib/validation/product";

interface ProductCommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  readonly onSelectProduct: (product: Product) => void;
  readonly onAdjustStock: (product: Product) => void;
  readonly onExportCsv: () => void;
  readonly onNewProduct: () => void;
  readonly onImportCsv: () => void;
  readonly onViewAlerts: () => void;
  readonly onViewApprovals: () => void;
}

/**
 * Command palette (Cmd/Ctrl+K) for the admin products page.
 * Uses cmdk for fuzzy search and framer-motion for enter/exit animations.
 */
export function ProductCommandPalette({
  open,
  onOpenChange,
  products,
  onSelectProduct,
  onExportCsv,
  onNewProduct,
  onImportCsv,
  onViewAlerts,
  onViewApprovals,
}: ProductCommandPaletteProps) {
  const { t } = useTranslation("admin");
  const [search, setSearch] = useState("");

  // Reset search on close
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Close on Escape (cmdk handles arrow keys / enter natively)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const runAction = (fn: () => void) => {
    onOpenChange(false);
    // Defer to next tick so the dialog unmounts cleanly before any modal opens
    window.setTimeout(fn, 0);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />
          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            role="dialog"
            aria-label={t("products.command_palette.placeholder")}
            className="fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2 px-4"
          >
            <Command
              className="rounded-xl border border-stone-200 bg-white shadow-2xl"
              shouldFilter={true}
              loop
            >
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={t("products.command_palette.placeholder")}
                className="w-full border-b border-stone-100 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-stone-400"
              />
              <Command.List className="max-h-80 overflow-y-auto p-2">
                <Command.Empty className="px-4 py-6 text-center text-sm text-stone-400">
                  {t("products.command_palette.no_results")}
                </Command.Empty>

                {/* Actions group */}
                <Command.Group
                  heading="Actions"
                  className="text-xs text-stone-400 px-2 py-1"
                >
                  <Command.Item
                    onSelect={() => runAction(onNewProduct)}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                  >
                    <Plus className="h-4 w-4 text-stone-400" />
                    {t("products.command_palette.new_product")}
                  </Command.Item>
                  <Command.Item
                    onSelect={() => runAction(onImportCsv)}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                  >
                    <Upload className="h-4 w-4 text-stone-400" />
                    {t("products.command_palette.import_csv")}
                  </Command.Item>
                  <Command.Item
                    onSelect={() => runAction(onExportCsv)}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                  >
                    <Download className="h-4 w-4 text-stone-400" />
                    {t("products.command_palette.export_view")}
                  </Command.Item>
                  <Command.Item
                    onSelect={() => runAction(onViewAlerts)}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                  >
                    <Bell className="h-4 w-4 text-stone-400" />
                    {t("products.command_palette.stock_alerts")}
                  </Command.Item>
                  <Command.Item
                    onSelect={() => runAction(onViewApprovals)}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                  >
                    <ClipboardCheck className="h-4 w-4 text-stone-400" />
                    {t("products.command_palette.pending_approvals")}
                  </Command.Item>
                </Command.Group>

                {/* Products group */}
                <Command.Group
                  heading="Produits"
                  className="text-xs text-stone-400 px-2 py-1"
                >
                  {products.slice(0, 20).map((product) => (
                    <Command.Item
                      key={product.id}
                      value={`${product.name} ${product.sku}`}
                      onSelect={() => runAction(() => onSelectProduct(product))}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-stone-100 aria-selected:bg-stone-100"
                    >
                      <Search className="h-3.5 w-3.5 text-stone-400" />
                      <span className="flex-1 truncate">{product.name}</span>
                      <span className="text-xs text-stone-400">
                        {product.sku}
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              </Command.List>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
